import Nav from "react-bootstrap/Nav"
import NavDropdown from "react-bootstrap/NavDropdown"

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as faIcons  from '@fortawesome/free-solid-svg-icons'

import useBranding from '../../hooks/useBranding'
import {useMenu} from '../CustomizationDashboard/hooks'
import {Link,useHistory} from 'react-router-dom'

import {capitalize} from '../../util/util'

import { toggleFirstLogin } from '../../state/onboarding'

import useCurrentUser from '../../hooks/useCurrentUser'

import store from '../../app/store'

import {setShow as setShowAIDemo} from '../AIDemo/slice'

import css from './SideNav.module.css'

import {
	selectShow as selectShowMessageCenter,
	setShow as setShowMessageCenter,
	selectMemberPrivateRoomId,
	getChatRoom,
} from '../MessageCenter/messageCenterSlice'

//type constants 

const REACT_LINK = 'reactLink'
const FUNCTION = 'function'
const NON_REACT_FITSW = 'notReactFitsw'
const EXTERNAL_LINK = 'externalLink'

//if the link doesn't require any data occuring in nested scope
const STATIC_LINK = 'staticLink'
//if the link requires data from nested scope/state (eg user_id_number)
const DYNAMIC_LINK =  'dynamicLink'

const DEFAULT_ROOT_TEXT_COLOR = '#fff'
const DEFAULT_SUBITEM_TEXT_COLOR = '#000'

//icons in the icon object use camel case 
//variants of the lisp case that is used 
//everywhere else.
const lispToCamel = (s)=>(
	`fa${s.replace('fa-','').split('-').map(capitalize).join('')}`
)

/**
 * Different features/menu items trigger different things
 * - Trigger a function 
 * - Link to another react page
 * - Link to external page
 * etc
 * 
 * This map points a feature key to an object like: 
 * 
 * {type: STRING, payload: OJBECT/FUNCTION/STRING}
 * 
 * Where the payload contains all data we need to 
 * correctly handling clicking the nav item
 * 
 * !!!READ ME!!! 
 * ALL FUNCTIONS MUST BE CURRIED SO YOU CAN INJECT
 * ANY INFORMATION YOU NEED THAT IS NOT AVAILABLE IN THE GLOBAL
 * SCOPE
 * !!!READ ME!!! 
 * 
 **/
const featureDefinitions = {
	dashboard:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'client_dashboard'
		}
	},
	gym_calendar:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'gym-calendar'
		}
	},
	growth:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'growth'
		}
	},
	client_progress:{
		type:REACT_LINK,
		payload:{
			type:DYNAMIC_LINK,
			generateLink:({user})=>`metric-graphs?clientId=${user.account_type==='client'?user.user_id_number:'all'}`
		}
	},
	progress_graphs:{//only here for backwards compatibility does same thing as client_progress
		type:REACT_LINK,
		payload:{
			type:DYNAMIC_LINK,
			generateLink:({user})=>`metric-graphs?clientId=${user.user_id_number}`
		}
	},
	exercise_progress:{
		type:REACT_LINK,
		payload:{
			type:DYNAMIC_LINK,
			generateLink:({user})=>`exercise_progress/?client=${user.user_id_number}`
		}
	},
	wearable_health:{
		type:REACT_LINK,
		payload:{
			type:DYNAMIC_LINK,
			generateLink:({user})=>`wearables?clientId=${user.user_id_number}`
		}
	},
	photos:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'photo_reel'
		}
	},
	exercise_library:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'exerciseList'
		}
	},
	food_library:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'foodlist'
		}
	},
	recipe_library:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'recipeList'
		}
	},
	program_library:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'programs'
		}
	},
	resources:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'resources'
		}
	},
	on_demand:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'on-demand'
		}
	},
	custom_forms:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'customForms'
		}
	},
	financials:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'financials_dashboard'
		}
	},
	scheduler:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'scheduler'
		}
	},
	trainer_page:{
		type:NON_REACT_FITSW,
		payload:{
			type:DYNAMIC_LINK,
			generateLink:({user})=>user.trainer_data?.trainerPageAddress
		}
	},
	blog:{
		type:REACT_LINK,
		payload:{
			type:DYNAMIC_LINK,
			generateLink:({user})=>{
				switch(user.account_type){
					case "client": return `${user.trainer_data?.trainerPageAddress}/blog/`
					default: return 'myBlog'
				}
			}
		}
	},
	live_classes:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'classes'
		}
	},
	help_center:{
		type:NON_REACT_FITSW,
		payload:{
			type:DYNAMIC_LINK,
			generateLink:({user})=>{
				switch(user.account_type){
					case "client": return 'help/for-clients/'
					case "trainer": return 'help/for-trainers/'
					case "gym": return 'help/for-gyms/'
					case "chain": return 'help/for-chains/'
					default: return '/'
				}
			}
		}
	},
	welcome_video:{
		type:FUNCTION,
		//observe: this is a curried function
		payload:(context)=>()=>{
			store.dispatch(toggleFirstLogin())
		}
	},
	clients:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'trainer'
		}
	},
	client_calendar:{
		type:REACT_LINK,
		payload:{
			type:DYNAMIC_LINK,
			generateLink:({user})=>{
				switch(user.account_type){
					case "trainer": return 'eventsView?calendarEventType=all&view=calendar&selector=All&filter=client'
					case "client": return 'eventsView?calendarEventType=all&view=calendar&selector=All'
					default: return 'eventsView?calendarEventType=all&view=calendar&selector=All&filter=client'
				}
			}
		}
	},
	metrics:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'metricList'
		}
	},
	favorite_tasks:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'eventsView?display=all&selector=Tasks&filter=favorite&client=favorite&view=list'
		}
	},
	favorite_nutrition:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'eventsView?display=all&selector=Nutrition&filter=favorite&client=favorite&view=list'
		}
	},
	favorite_workouts:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'eventsView?display=all&selector=Workouts&filter=favorite&client=favorite&view=list'
		}
	},
	gym_overview:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'gym'
		}
	},
	all_trainers:{
		type:REACT_LINK,
		payload:{
			type:DYNAMIC_LINK,
			generateLink:({user})=>{
				switch(user.account_type){
					case "chain": return 'all-trainers'
					default: return 'trainers'
				}
			}
		}
	},
	all_clients:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'clients'
		}
	},
	corporate_overview:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'chain'
		}
	},
	admin_dashboard:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'adminDashboard'
		}
	},
	all_gyms:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'all-gyms'
		}
	},
	back_to_gym:{
		type:FUNCTION,
		//curried function
		payload:()=>()=>window.location = '/trainers'
	},
	fitswai:{
		type:FUNCTION,
		payload:()=>store.dispatch.bind(null,setShowAIDemo(true))
	},
	timer:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'timer'
		}
	},
	max_schedule:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'max_schedule'
		}
	},
	max_support:{
		type:EXTERNAL_LINK,
		payload:{
			type:STATIC_LINK,
			link:'https://forms.gle/HfiYXbY4X6LHwMT67'
		}
	},
	profile:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'profile'
		}
	},
	my_trainer:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'my-trainer'
		}
	},
	max_tracker:{
		type:FUNCTION,
		payload:({redirect})=>()=>{
			const {todaysEvents} = store.getState()
			if(todaysEvents.nutrition.ids.length===0){
				window.alert('No nutrition plans today')
			}else{
				const nutrition_id = todaysEvents.nutrition.ids[0]
				const link = `/eventsView/?selector=Nutrition&display=${nutrition_id}&view=list`
				redirect(link)
			}
		}
	},
	tasks:{
		type:REACT_LINK,
		payload:{
			type:DYNAMIC_LINK,
			generateLink:({user})=>{
				switch(user.account_type){
					case "client": return `eventsView?calendarEventType=task&view=calendar&selector=Tasks`
					default: return ""
				}
			}
		}
	},
	notifications:{
		type:REACT_LINK,
		payload:{
			type:STATIC_LINK,
			link:'notification_dashboard'
		}
	},
	message_center:{
		type:FUNCTION,
		payload:()=>()=>{
			const state = store.getState()
			const trainer_id = state.users.current_user.trainer_data?.trainer_id
			const room_id = selectMemberPrivateRoomId(state,trainer_id)
			store.dispatch(setShowMessageCenter(true))
			store.dispatch(getChatRoom({room_id}))

		}
	}
}

//DEFAULT goes nowhere to prevent crashes
const DEFAULT_FEATURE_DEFINITION = {
	type:REACT_LINK,
	payload:{
		type:STATIC_LINK,
		link:'/'
	}
}

const getFeatureKeyDefinition = (key)=>(
	featureDefinitions[key] || DEFAULT_FEATURE_DEFINITION
)

const addTrainerParam = (link)=>{
	const splitLink = link.split('?')
	const locationParams = new URLSearchParams(window.location.search)
	const trainerParam = locationParams.get('trainer')

	if(!Boolean(trainerParam)) return link

	if(splitLink.length===1) return `${splitLink}?trainer=${trainerParam}`

	const linkParams = new URLSearchParams(`?${splitLink.at(-1)}`)
	linkParams.set('trainer',trainerParam)

	return `${splitLink[0]}?${linkParams.toString()}`
}

const Icon = ({icon})=>{
	return (
		<FontAwesomeIcon  
			icon={faIcons[lispToCamel(icon)]}
			className="mr-1"
		/>
	)
}

const NavItem = ({item,isSubItem})=>{
	const featureDefinition = getFeatureKeyDefinition(
		item.feature_key
	)
	const user = useCurrentUser()
	const customBranding = useBranding()
	const history = useHistory()

	const redirect = (link)=>{
		history.push(link)
	}

	const context = {user,redirect}

	const DEFAULT_TEXT_COLOR = (
		isSubItem 
		? DEFAULT_SUBITEM_TEXT_COLOR 
		: DEFAULT_ROOT_TEXT_COLOR
	)

	const COLOR = (
		Boolean(customBranding) &&!isSubItem 
		? customBranding.textColor 
		: DEFAULT_TEXT_COLOR
	)

	switch(featureDefinition.type){
		case REACT_LINK: return (
			<Link 
				className={css.NavLink}
				style={{
					color: COLOR,
					padding:'.5rem 1rem'
				}} 
				to={
					featureDefinition.payload.type === STATIC_LINK
					//static links don't require any additional context outside
					//of the global scope
					? `/${addTrainerParam(featureDefinition.payload.link)}`
					//dynamic links require additional context from neste scope/state
					//these use a genereLink method that expects all required context
					//eg user data and whate ever else is needed
					: `/${addTrainerParam(featureDefinition.payload.generateLink(context))}`
				}
			>
				{!isSubItem&&<Icon icon={item.icon}/>}
				{item.name}
			</Link>
		)
		case NON_REACT_FITSW: return (
			<a 
				className={css.NavLink}
				style={{
					color: COLOR,
					padding:'.5rem 1rem'
				}} 
				href={
					featureDefinition.payload.type === STATIC_LINK
					//static links don't require any additional context outside
					//of the global scope
					? `/${addTrainerParam(featureDefinition.payload.link)}`
					//dynamic links require additional context from neste scope/state
					//these use a genereLink method that expects all required context
					//eg user data and whate ever else is needed
					: `/${addTrainerParam(featureDefinition.payload.generateLink(context))}`
				}
			>
				{!isSubItem&&<Icon icon={item.icon}/>}
				{item.name}
			</a>
		)
		case EXTERNAL_LINK: return (
			<a 
				target="_blank"
				className={css.NavLink}
				style={{
					color: COLOR,
					padding:'.5rem 1rem'
				}} 
				href={
					featureDefinition.payload.type === STATIC_LINK
					//static links don't require any additional context outside
					//of the global scope
					? `${addTrainerParam(featureDefinition.payload.link)}`
					//dynamic links require additional context from neste scope/state
					//these use a genereLink method that expects all required context
					//eg user data and whate ever else is needed
					: `${addTrainerParam(featureDefinition.payload.generateLink(context))}`
				}
			>
				{!isSubItem&&<Icon icon={item.icon}/>}
				{item.name}
			</a>
		)
		//functions allow for more custom behavior like dispatching a redux action
		//to open a modal
		case FUNCTION: return (
			<span 
				className={css.NavLink}
				style={{
					color: COLOR,
					cursor:'pointer',
					padding:'.5rem 1rem'
				}} 
				onClick={featureDefinition.payload(context)}
			>
				{!isSubItem&&<Icon icon={item.icon}/>}
				{item.name}
			</span>
		)
		//this is a CATCH to prevent crashes
		default: return (
			<span 
				className={css.NavLink}
				style={{
					color: COLOR,
					cursor:'pointer',
					padding:'.5rem 1rem'
				}} 
				className="DEV_WARNING_TYPE_NOT_SUPPORTED"
			>
				{!isSubItem&&<Icon icon={item.icon}/>}
				{item.name}
			</span>
		)
	}
}

const NavGroup = ({group})=>{
	const customBranding = useBranding()
	return (
		<NavDropdown
			title={
				<span
					className={css.NavLink}
					style={{
						color: Boolean(customBranding) ? customBranding.textColor : DEFAULT_ROOT_TEXT_COLOR,
						padding:0
					}}
				>
					<Icon icon={group.icon}/>
					{group.name}
				</span>
			}
		>
			{group.nav_items.map(item=>(
				<span className="nav-link">
					<NavItem item={item} isSubItem/>
				</span>
			))}
		</NavDropdown>
	)
}

export default function SideNav(){
	const customBranding = useBranding()
	const {status,items} = useMenu()
	const user = useCurrentUser()
	const homeLink = {
		client:'client_dashboard',
		prospect:'client_dashboard',
		trainer:'trainer',
		gym:'gym',
		chain:'chain'
	}[user.account_type]

	return (
		<div className="d-flex flex-column" style={{ height: '100%' }}>
			<div className="py-4" style={{ flex: "0 0 auto" }}>
				<Link to={`/${homeLink}`}>
					<img
						width={customBranding.width + "px"}
						className="d-block m-auto"
						src={
							customBranding
							? `${customBranding.logo}`
							:"https://cdn.fitsw.com/assets/img/brand/FitSW-Personal-Trainer-Software-Logo-For-Fitness-Coaches.png"
						}
						alt="logo"
					/>
				</Link>
			</div>
			<Nav
				className="flex-column"
				style={{
					flex: "1 1 auto",
					overflow: "auto",
					flexWrap: "nowrap",
					marginLeft: "32px"
				}}
			>
				{items.map(item=>(
					item.item_type==='nav_item'
					? <NavItem key={item.id} item={item}/>
					: <NavGroup key={item.id} group={item}/>
				))}
			</Nav>
		</div>
	)
}